import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PiCalendarBlank, PiArrowLeft, PiArrowSquareOut, PiLightbulb } from 'react-icons/pi';
import { eventService } from '../../../services/event.service';
import styles from './PublicEventDetail.module.css';

const getImageSrc = (url) => {
    if (!url) return null;
    if (url.startsWith('data:') || url.startsWith('http') || url.startsWith('/')) return url;
    return `data:image/jpeg;base64,${url}`;
};

const formatDate = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString('uk-UA', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
    });
};

const formatDateRange = (start, end) => {
    const s = formatDate(start);
    const e = formatDate(end);
    if (!s && !e) return '';
    if (!e) return s;
    return `${s} — ${e}`;
};

const getTimeStatus = (startDate, endDate) => {
    if (!startDate || !endDate) return 'upcoming';
    const now = new Date();
    if (now < new Date(startDate)) return 'upcoming';
    if (now > new Date(endDate)) return 'ended';
    return 'active';
};

function PublicEventDetail() {
    const { id } = useParams();
    const { t } = useTranslation('common');
    const { t: tValidation } = useTranslation('validation');
    const [event, setEvent] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!id) return;
        const load = async () => {
            setIsLoading(true);
            try {
                const data = await eventService.getPublicEventById(id);
                setEvent(data?.event ?? data);
            } catch (err) {
                const errorKey = err?.payload?.error;
                setError(typeof errorKey === 'string' ? errorKey : 'network_error');
            } finally {
                setIsLoading(false);
            }
        };
        load();
    }, [id]);

    const timeStatus = event ? getTimeStatus(event.start_date, event.end_date) : null;
    const coverSrc = event ? getImageSrc(event.cover_image) : null;
    const tags = event?.tags ?? [];

    return (
        <div className={styles.page}>
            <nav className={styles.navbar}>
                <div className={styles.logoWrap}>
                    <img src="/assets/menu-logo-mini.svg" alt="Logo" className={styles.logoIcon} />
                    <span className={styles.logoText}>IDEARUM</span>
                </div>
                <Link to="/" className={styles.signInLink}>
                    {t('events.sign_in')}
                </Link>
            </nav>

            <main className={styles.content}>
                <Link to="/events" className={styles.backLink}>
                    <PiArrowLeft className={styles.backIcon} />
                    {t('events.back_to_list')}
                </Link>

                {isLoading && (
                    <div className={styles.stateBox}>
                        <div className={styles.spinner} />
                        <p className={styles.stateText}>Завантаження...</p>
                    </div>
                )}

                {!isLoading && error && (
                    <div className={styles.stateBox}>
                        <p className={styles.errorText}>{tValidation(error)}</p>
                    </div>
                )}

                {!isLoading && !error && event && (
                    <article className={`${styles.card} ${styles[`card_${timeStatus}`]}`}>
                        {coverSrc && (
                            <div className={styles.coverWrap}>
                                <img src={coverSrc} alt={event.title} className={styles.cover} />
                            </div>
                        )}

                        <div className={styles.body}>
                            {timeStatus === 'active' && (
                                <span className={styles.activeBadge}>
                                    <span className={styles.activeDot} />
                                    {t('events.status_now')}
                                </span>
                            )}
                            {timeStatus === 'upcoming' && (
                                <span className={styles.upcomingBadge}>
                                    {t('events.status_upcoming_label')}
                                </span>
                            )}

                            <h1 className={styles.title}>{event.title}</h1>

                            {(event.start_date || event.end_date) && (
                                <div className={styles.dateRow}>
                                    <PiCalendarBlank className={styles.dateIcon} />
                                    <span>{formatDateRange(event.start_date, event.end_date)}</span>
                                </div>
                            )}

                            {tags.length > 0 && (
                                <div className={styles.tags}>
                                    {tags.map((tag, i) => (
                                        <span key={i} className={styles.tag}>
                                            {tag.name ?? tag.code ?? tag}
                                        </span>
                                    ))}
                                </div>
                            )}

                            <div className={styles.divider} />

                            {event.description && (
                                <p className={styles.description}>{event.description}</p>
                            )}

                            <div className={styles.actions}>
                                {event.organizer_link && (
                                    <a
                                        href={event.organizer_link}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className={styles.organizerBtn}
                                    >
                                        <PiArrowSquareOut className={styles.btnIcon} />
                                        {t('events.organizer_link')}
                                    </a>
                                )}
                                <Link to="/" className={styles.publishBtn}>
                                    <PiLightbulb className={styles.btnIcon} />
                                    {t('events.publish_idea')}
                                </Link>
                            </div>
                        </div>
                    </article>
                )}
            </main>
        </div>
    );
}

export default PublicEventDetail;
